import { FolderKanban } from 'lucide-react';
import { Badge } from '@proerp/ui';
import { describeError } from '@/lib/apiClient';
import { formatMoney } from '@/lib/money';
import { usePermissions } from '@/lib/useCurrentUser';
import { ProjectAmpelBadge } from '@/features/projects/statusBadge';
import { useDashboardKpis } from '../dashboardApi';
import { TaskCard, TaskRow } from './TaskCard';

/**
 * Projektgesundheit: Ampel-Verteilung + die kritischsten laufenden Projekte.
 *
 * Beides rechnet der Server über ALLE laufenden Projekte (`projectHealth` in
 * `GET /v1/dashboard/kpis`). Bis 2026-09-29 zählte das Widget die ersten 200
 * der Projektliste (neueste zuerst) — ältere, überfällige Projekte fehlten dann
 * in der Ampel und in der Liste, und es hieß „alles grün".
 *
 * Der Auftragswert je Zeile ist eine Finanzangabe: der Server liefert ihn nur
 * mit dashboard.viewfinancials und dem Auftragswert-Recht der Projektliste
 * (ADR 0062); die Anzeige verlangt zusätzlich dashboard.viewfinancials.
 */
export function ProjectHealthWidget() {
  const { can } = usePermissions();
  const kpis = useDashboardKpis();

  const health = kpis.data?.data.projectHealth;
  const error = kpis.isError ? describeError(kpis.error) : null;
  const critical = health?.critical ?? [];
  const canViewFinancials = can('dashboard.viewfinancials');

  return (
    <TaskCard
      title="Projektgesundheit"
      icon={FolderKanban}
      isLoading={kpis.isLoading}
      error={error}
      notice={health === null ? 'Projektdaten sind für deine Rolle nicht freigegeben.' : null}
      isEmpty={critical.length === 0}
      emptyText="Alle aktiven Projekte im grünen Bereich."
      header={
        health && (
          <span className="flex items-center gap-2 text-xs">
            <Badge tone="success">Grün {health.ampel.GRUEN}</Badge>
            <Badge tone="warning">Gelb {health.ampel.GELB}</Badge>
            <Badge tone="danger">Rot {health.ampel.ROT}</Badge>
          </span>
        )
      }
    >
      {critical.map((project) => (
        <TaskRow
          key={project.id}
          to={`/projekte/${project.id}`}
          left={
            <>
              <span className="font-mono text-xs text-muted-foreground">{project.code}</span>
              <span className="ml-2 text-foreground">{project.name}</span>
              {project.customerName && (
                <span className="ml-2 text-xs text-muted-foreground">{project.customerName}</span>
              )}
            </>
          }
          right={
            <span className="flex items-center gap-2">
              {canViewFinancials && project.orderValue && (
                <span className="tabular-nums text-foreground">{formatMoney(project.orderValue)}</span>
              )}
              <ProjectAmpelBadge ampel={project.ampel} />
            </span>
          }
        />
      ))}
    </TaskCard>
  );
}
